class OpenclawService {
  constructor() {
    const isProduction = process.env.NODE_ENV === 'production';
    this.baseURL = window.__openclaw_url || process.env.REACT_APP_OPENCLAW_URL || 'https://openyc-api-backend.fly.dev';
    this.apiKey = window.__openclaw_api_key || process.env.REACT_APP_OPENCLAW_API_TOKEN || '';
    this.instance = null;
    this.activeConversationId = null;
  }

  getBaseURL() {
    return this.baseURL;
  }
  
  getAPIKey() {
    return window.__openclaw_api_key || process.env.REACT_APP_OPENCLAW_API_TOKEN || '';
  }

  getAuthHeaders() {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('firebase_token');
    const apiKey = this.getAPIKey();
    const authToken = token || apiKey;
    return {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    };
  }

  async ensureInstance() {
    if (this.instance) return this.instance;
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/instance`, {
        headers: this.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.instance = data.instance;
          return this.instance;
        }
      }
    } catch (err) {
      console.warn('[OpenClaw] Failed to fetch instance:', err.message);
    }
    return null;
  }

  async updateInstance(config) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/instance`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(config)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.instance = data.instance;
          return this.instance;
        }
      }
    } catch (err) {
      console.error('[OpenClaw] Failed to update instance:', err.message);
    }
    return null;
  }

  async listConversations(limit = 50) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/conversations?limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        return data.success ? data.conversations : [];
      }
    } catch (err) {
      console.error('[OpenClaw] Failed to list conversations:', err.message);
    }
    return [];
  }

  async createConversation(title) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/conversations`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ title })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.activeConversationId = data.conversation.id;
          return data.conversation;
        }
      }
    } catch (err) {
      console.error('[OpenClaw] Failed to create conversation:', err.message);
    }
    return null;
  }

  async deleteConversation(conversationId) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (this.activeConversationId === conversationId) {
          this.activeConversationId = null;
        }
        return data.success;
      }
    } catch (err) {
      console.error('[OpenClaw] Failed to delete conversation:', err.message);
    }
    return false;
  }

  async getConversationMessages(conversationId, limit = 50) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/openclaw/conversations/${conversationId}/messages?limit=${limit}`, {
        headers: this.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        return data.success ? data.messages : [];
      }
    } catch (err) {
      console.error('[OpenClaw] Failed to get messages:', err.message);
    }
    return [];
  }

  setActiveConversation(conversationId) {
    this.activeConversationId = conversationId;
  }

  async generateReply(message, context = [], userId = 'anonymous', chatId = 'default') {
    try {
      const baseURL = this.getBaseURL();

      const formattedContext = context.map(msg => ({
        content: msg.text || msg.content || msg.message || '',
        isAI: msg.sender === 'ai' || msg.isAI === true
      }));

      const response = await fetch(`${baseURL}/api/moltbot/reply`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          message,
          userId,
          chatId,
          conversationId: this.activeConversationId,
          context: formattedContext.slice(-5)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Molbot reply response:', JSON.stringify(data, null, 2).substring(0, 800));
      
      // Extract reply from payloads
      let replyContent = '';
      
      if (data.payloads && data.payloads.length > 0) {
        replyContent = data.payloads[0]?.text || data.payloads[0]?.content || '';
      }

       
       // Return whatever we got (even if empty)
       return {
         content: replyContent,
         isAI: true,
         senderId: 'ai_openclaw',
         senderName: 'AI_Matrix',
         timestamp: new Date(),
         metadata: {
           model: data.agentMeta?.model,
           provider: data.agentMeta?.provider,
           usage: data.agentMeta?.usage,
           success: !data.aborted,
           durationMs: data.meta?.durationMs
         }
       };
    } catch (error) {
      console.error('OpenClaw API 调用失败:', error);
      // Return error info instead of fallback
      return {
        content: `API Error: ${error.message}`,
        isAI: true,
        senderId: 'ai_openclaw',
        senderName: 'AI_Matrix',
        timestamp: new Date(),
        metadata: { success: false, error: error.message }
      };
    }
  }

  async generateReplyStream(message, context = [], userId = 'anonymous', chatId = 'default', onChunk) {
    try {
      const formattedContext = context.map(msg => ({
        content: msg.text || msg.content || msg.message || '',
        isAI: msg.sender === 'ai' || msg.isAI === true
      }));

      const baseURL = this.getBaseURL();
      
      const response = await fetch(`${baseURL}/api/moltbot/reply`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          message,
          userId,
          chatId,
          conversationId: this.activeConversationId,
          context: formattedContext.slice(-5),
          stream: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      
      let replyContent = '';
      if (data.payloads && data.payloads.length > 0) {
        replyContent = data.payloads[0]?.text || data.payloads[0]?.content || '';
      } else if (data.reply) {
        replyContent = data.reply;
      } else if (data.response) {
        replyContent = data.response;
      } else {
        replyContent = data.message || '';
      }

      const chars = replyContent.split('');
      let currentText = '';
      
      for (let i = 0; i < chars.length; i++) {
        currentText += chars[i];
        onChunk(currentText, i === chars.length - 1);
        
        const delay = Math.random() * 30 + 10;
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      return {
        content: replyContent,
        isAI: true,
        senderId: 'ai_openclaw',
        senderName: 'AI_Matrix',
        timestamp: new Date(),
        metadata: {
          model: data.agentMeta?.model,
          usage: data.agentMeta?.usage,
          success: data.success
        }
      };
    } catch (error) {
      console.error('Openclaw API 流式调用失败:', error);
      throw error;
    }
  }

  async generateSimple(prompt) {
    return this.generateReply(prompt, [], 'anonymous', 'simple');
  }

  async getChatHistory(chatId, userId) {
    try {
      const response = await fetch(`${this.baseURL}/api/chat/history/${chatId}?userId=${userId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('获取对话历史失败:', error);
      return { success: false, history: [] };
    }
  }

  async clearChatHistory(chatId, userId) {
    try {
      const response = await fetch(`${this.baseURL}/api/chat/history/${chatId}?userId=${userId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('清除对话历史失败:', error);
      return { success: false, error: error.message };
    }
  }

  async sendMediaMessage(mediaUrl, mediaType, caption = '', duration = 0) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/messages/send/media`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          to: this.activeConversationId || 'default',
          mediaUrl,
          mediaType,
          caption,
          duration
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.message || { success: true };
    } catch (error) {
      console.error('发送媒体消息失败:', error);
      return { success: false, error: error.message };
    }
  }

  async sendGroupMessage(to, content, groupId) {
    try {
      const response = await fetch(`${this.getBaseURL()}/api/v1/messages/send`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          to,
          content,
          type: 'group',
          groupId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.message || { success: true };
    } catch (error) {
      console.error('发送群聊消息失败:', error);
      return { success: false, error: error.message };
    }
  }

  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET'
      });
      
      if (response.ok) {
        const data = await response.json();
        return { ok: true, data };
      }
      
      return { ok: false, error: `HTTP ${response.status}` };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  }
}

const openclawService = new OpenclawService();

export default openclawService;
